<template>
  <div class="sign-in-container">
    <div>
      <v-dialog
        v-model="authDialog"
        max-width="350"
      >
        <v-card>
          <v-card-title class="text-h5"> 2-Step Verification </v-card-title>
          <v-card-text>
            Please enter the 6-digit verification code sent to your phone.
            <v-text-field
              v-model="token"
              :disabled="verifyLoading"
              label="Code"
              autofocus
              variant="underlined"
              @keyup="
                (event) => {
                  if (event.key === 'Enter') {
                    verifyCode();
                  }
                }
              "
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              :disabled="verifyLoading"
              variant="flat"
              @click="authDialog = false"
            >
              Cancel
            </v-btn>
            <v-btn
              :loading="verifyLoading"
              :disabled="verifyLoading"
              color="secondary"
              variant="flat"
              @click="verifyCode"
            >
              Submit
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
    <NotificationCard
      v-if="config.status"
      :title="config.status.title"
      :message="config.status.message"
      :level="config.status.level"
    />
    <div
      v-if="resolvingSso"
      class="re-fill-layout"
    >
      <v-progress-circular
        indeterminate
        class="text-primary loader"
      />
    </div>
    <div
      v-else
      class="re-fill-layout"
    >
      <div
        class="disable-flex-grow"
        justify-center
      >
        <img
          class="bainbridge-name-horiz mx-4"
          src="~@/assets/images/BH-Horizontal.svg"
        />
      </div>
      <div
        class="disable-flex-grow"
        justify-center
      >
        <v-card class="mt-6 input-container">
          <v-card-title class="text-white bg-primary pt-1">
            <div>
              <p class="med-os-tag">Med O.S.<sup style="font-size: 0.6em; top: -0.5em">®</sup></p>
              {{ state }}
            </div>
          </v-card-title>
          <v-card-text class="pa-6">
            <transition
              name="slide-x-transition"
              mode="out-in"
            >
              <div>
                <v-skeleton-loader
                  v-if="!loginComplete"
                  :loading="loading"
                  height="94"
                  type="list-item-two-line"
                />
                <EnterEmail
                  v-if="page === 'email' && loginComplete"
                  :passed-identity="email"
                  :loading="loading"
                  @ssoLogin="ssoLogin"
                  @setIdentity="setIdentity"
                  @switchPage="switchPage"
                />
                <EnterPassword
                  v-else-if="page === 'password' && loginComplete"
                  :email="email"
                  :loading="loading"
                  @login="passwordLogin"
                  @switchPage="switchPage"
                />
                <ForgotPassword
                  v-else-if="page === 'forgot' && loginComplete"
                  :passed-email="email"
                  @switchPage="switchPage"
                />
              </div>
            </transition>
            <div
              id="recaptcha-container"
              style="display: none"
            />
            <div class="clearfix" />
          </v-card-text>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import {
  GoogleAuthProvider,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  SAMLAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
} from 'firebase/auth';

import { EventLog } from '@/lib/event-log';
import { GOOGLE_IDENTITY_PROVIDER } from '@/lib/identity-methods';

import EnterEmail from './signin/EnterEmail.vue';
import EnterPassword from './signin/EnterPassword.vue';
import ForgotPassword from './signin/ForgotPassword.vue';
import NotificationCard from './notification/NotificationCard/NotificationCard.vue';

export default {
  name: 'LoginPortal',
  components: {
    EnterEmail,
    EnterPassword,
    ForgotPassword,
    NotificationCard,
  },
  data() {
    return {
      page: 'email',
      loading: false,
      verifyLoading: false,
      authDialog: false,
      email: null,
      identityTenant: null,
      localIdentityProvider: null,
      authentication: null,
      resolver: null,
      verificationId: null,
      token: null,
      recaptchaVerifier: null,
      state: 'Login',
      ssoReturnPending: Boolean(localStorage.getItem('signinEmail')),
      sawFirebaseSession: false,
      // Firebase unsubscribe handle, torn down in beforeUnmount.
      unsubscribeAuth: null,
    };
  },
  computed: {
    resolvingSso() {
      if (!this.ssoReturnPending) return false;
      return this.sawFirebaseSession || !this.loginComplete;
    },
    ...mapGetters(['identityProvider', 'loginComplete', 'multiFactorRequired', 'user', 'config', 'configAuth']),
  },
  watch: {
    authDialog: {
      handler() {
        if (!this.authDialog) {
          this.token = null;
          this.loading = false;
          this.verifyLoading = false;
        }
      },
      deep: true,
      immediate: true,
    },
    loginComplete: {
      handler() {
        if (this.loginComplete && this.user) {
          this.redirectToPath();
          return;
        }
        if (this.loginComplete && !this.$firebaseAuth?.currentUser) {
          this.ssoReturnPending = false;
        }
      },
      immediate: true,
    },
  },
  created() {
    if (!this.ssoReturnPending) return;
    this.sawFirebaseSession = Boolean(this.$firebaseAuth?.currentUser);
    if (!this.sawFirebaseSession && this.$firebaseAuth) {
      this.unsubscribeAuth = onAuthStateChanged(this.$firebaseAuth, (firebaseUser) => {
        if (firebaseUser) {
          this.sawFirebaseSession = true;
        }
      });
    }
  },
  mounted() {
    if (this.user) {
      this.redirectToPath();
    }
  },
  beforeUnmount() {
    if (this.unsubscribeAuth) {
      this.unsubscribeAuth();
    }
  },
  methods: {
    switchPage(page) {
      this.page = page;
      if (page === 'forgot') {
        this.state = 'Request Password Reset';
      } else {
        this.state = 'Login';
      }
    },
    setIdentity(identityData) {
      this.email = identityData.email;
      this.identityTenant = identityData.tenant;
      this.localIdentityProvider = identityData.provider;
      this.setIdentityProvider(this.localIdentityProvider);
      this.page = 'password';
    },
    emitFailLog(error, message) {
      const failLog = new EventLog({
        event: 'account.fail_login',
        email: this.email,
        identityTenant: this.identityTenant,
        identityProvider: this.localIdentityProvider,
        error,
      });
      this.$services.users.postTrackingLog(failLog);
      this.$notify(message);
    },
    emitSuccessLog() {
      const successLog = new EventLog({
        event: 'account.log_in',
        email: this.email,
        identityTenant: this.identityTenant,
        identityProvider: this.localIdentityProvider,
      });
      this.$services.users.postTrackingLog(successLog);
    },
    passwordLogin(authentication) {
      this.loading = true;
      this.authentication = authentication;
      if (!this.recaptchaVerifier) {
        // Initialize reCAPTCHA verifier for multi-factor authentication
        this.recaptchaVerifier = new RecaptchaVerifier(this.$firebaseAuth, 'recaptcha-container', {
          size: 'invisible',
        });
      }
      signInWithEmailAndPassword(this.$firebaseAuth, this.email, this.authentication)
        .then((userCredential) => {
          if (this.configAuth === 'authv2') {
            userCredential.user.getIdToken(true).then((idToken) => {
              this.exchangeToken(idToken)
                .catch((error) => {
                  this.handleServerLoginError(error);
                })
                .finally(() => {
                  this.loading = false;
                });
            });
          } else {
            userCredential.user.getIdToken().then((idToken) => {
              this.setAccessToken(idToken);
              this.login()
                .catch((error) => {
                  this.handleServerLoginError(error);
                })
                .finally(() => {
                  this.loading = false;
                });
            });
          }
        })
        .catch((tokenMessage) => {
          if (tokenMessage.code === 'auth/multi-factor-auth-required') {
            this.resolver = tokenMessage.resolver;
            if (this.resolver.hints[0].factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
              const phoneInfoOptions = {
                multiFactorHint: this.resolver.hints[0],
                session: this.resolver.session,
              };
              const phoneAuthProvider = new PhoneAuthProvider(this.$firebaseAuth);
              phoneAuthProvider
                .verifyPhoneNumber(phoneInfoOptions, this.recaptchaVerifier)
                .then((verificationId) => {
                  this.verificationId = verificationId;
                  this.authDialog = true;
                })
                .catch((error) => {
                  this.emitFailLog(error.message, 'Verification failed.');
                  this.loading = false;
                });
            } else {
              this.emitFailLog(tokenMessage.message, 'Invalid email or password.');
              this.loading = false;
            }
          } else if (tokenMessage.code === 'auth/too-many-requests') {
            this.emitFailLog(tokenMessage.message, 'Too many failed login attempts. Please try again later.');
            this.loading = false;
          } else {
            this.emitFailLog(tokenMessage.message, 'Invalid email or password.');
            this.loading = false;
          }
        });
    },
    handleServerLoginError(error) {
      // The http.js wrapper rejects with json.error directly, so `error` is
      // typically the string 'expired_password' / 'account_locked' etc. Fall
      // back to nested shapes in case other callers/wrappers reject with an
      // object.
      const serverError = (typeof error === 'string' ? error : null) || error?.response?.data?.error || error?.code;

      if (serverError === 'expired_password') {
        this.emitFailLog('expired_password', 'Your password expired. Please click "Forgot Password?" to reset it.');
      } else if (serverError === 'account_locked') {
        this.emitFailLog('account_locked', 'Your account is locked. Please contact support to recover it.');
      } else {
        const detail = typeof error === 'string' ? error : error?.message || 'unknown';
        this.emitFailLog(detail, 'Invalid email or password.');
      }
    },
    verifyCode() {
      this.verifyLoading = true;
      const cred = PhoneAuthProvider.credential(this.verificationId, this.token);
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
      this.resolver
        .resolveSignIn(multiFactorAssertion)
        .then((userCredential) => {
          if (this.configAuth === 'authv2') {
            userCredential.user.getIdToken(true).then((idToken) => {
              this.exchangeToken(idToken)
                .catch((error) => {
                  this.emitFailLog(error.message, 'Login failed.');
                })
                .finally(() => {
                  this.authDialog = false;
                });
            });
          } else {
            userCredential.user.getIdToken().then((idToken) => {
              this.setAccessToken(idToken);
              this.login()
                .catch((error) => {
                  this.emitFailLog(error.message, 'Login failed.');
                })
                .finally(() => {
                  this.authDialog = false;
                });
            });
          }
        })
        .catch((error) => {
          this.emitFailLog(error.message, 'Verification failed.');
          this.authDialog = false;
        });
    },
    redirectToPath() {
      if (this.multiFactorRequired) {
        this.$router.push({ name: 'UserProfile' });
      } else if (this.$route.query.redirect) {
        this.$router.push(this.$route.query.redirect);
      } else {
        this.$router.push({ name: 'LandingPage' });
      }
    },
    requiresPopup() {
      const ua = navigator.userAgent;
      const isSafari = /^(?!.*Chrome).*Safari/.test(ua);
      const isFirefox = /Firefox/.test(ua);
      return isSafari || isFirefox;
    },
    ssoLogin(identityData) {
      this.loading = true;
      this.email = identityData.email;
      this.identityTenant = identityData.tenant;
      this.localIdentityProvider = identityData.provider;
      let provider;
      if (this.localIdentityProvider === GOOGLE_IDENTITY_PROVIDER) {
        provider = new GoogleAuthProvider(this.$firebaseAuth);
        provider.setCustomParameters({
          prompt: 'consent',
        });
      } else {
        provider = new SAMLAuthProvider(this.localIdentityProvider);
      }
      this.$firebaseAuth.tenantId = this.identityTenant;
      if (this.configAuth === 'authv2' && this.$firebaseAuth.currentUser) {
        this.$firebaseAuth.currentUser.getIdToken(true);
      }
      if (this.requiresPopup()) {
        signInWithPopup(this.$firebaseAuth, provider).catch((error) => {
          this.emitFailLog(
            error.message,
            'Login with this browser requires popups. Your browser blocked the popup. Please enable popups for this site.'
          );
          this.loading = false;
        });
      } else {
        signInWithRedirect(this.$firebaseAuth, provider).catch((error) => {
          this.emitFailLog(error.message, 'Login failed.');
          this.loading = false;
        });
      }
    },
    ...mapActions(['login', 'exchangeToken', 'setAccessToken', 'setIdentityProvider']),
  },
};
</script>

<style lang="scss" scoped>
@import '@/sass/main.scss';
.status-notification-container {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.status-notification {
  max-width: 300px;
  padding: 12px;
  border-radius: 6px;
  color: black;
  font-weight: 600;
  background-color: lighten($bainbridge-gold, 20);
  opacity: 0.7;
}

.sign-in-container {
  .re-fill-layout {
    margin-top: 5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 80vh;
  }

  .disable-flex-grow {
    flex-grow: 0;
  }

  .bainbridge-name-horiz {
    width: 600px;
  }

  .input-container {
    width: 580px;
  }
}

.med-os-tag {
  font-size: 18px;
  margin: 0;
}
</style>
